import { AnalyzedItem } from 'containers/QuickOrder/types';
import React, { Fragment, memo } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { AiMatchedProduct } from 'types/schema';
import ResultRow from './ResultRow';
import messages from './messages';

interface ResultTableProps {
  products: AnalyzedItem[];
  quantities: Record<string, number>;
  allSelected: boolean;
  someSelected: boolean;
  selectedCount: number;
  onToggleAll: () => void;
  onToggleRow: (id: string) => void;
  onQuantityChange: (id: string, value: number) => void;
  onChangeProduct: (id: string, anchor: HTMLElement) => void;
  onSwapProduct: (id: string, alt: AiMatchedProduct) => void;
  loggedIn: boolean;
  goToSignIn: () => void;
}

const Wrapper = styled.div`
  border: 1px solid #e4e7eb;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;
  background: #fff;
`;

const Checkbox = styled.button<{ checked: boolean; indeterminate?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${({ checked, indeterminate }) => (checked || indeterminate ? '#43A047' : '#D1D5DB')};
  background: ${({ checked, indeterminate }) => (checked || indeterminate ? '#43A047' : '#fff')};
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &::after {
    content: '';
    display: ${({ checked, indeterminate }) => (checked || indeterminate ? 'block' : 'none')};
    ${({ checked }) =>
      checked
        ? `
      width: 4px;
      height: 8px;
      border: solid #fff;
      border-width: 0 1.5px 1.5px 0;
      transform: rotate(45deg) translate(-1px, -1px);
    `
        : `
      width: 8px;
      height: 1.5px;
      background: #fff;
    `}
  }
`;

const TitleCol = styled.div`
  width: 310px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TitleText = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #4b5563;
`;

const SubtitleText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b5563;
`;

const ColLabel = styled.div<{ width: number; preLine?: boolean }>`
  width: ${({ width }) => width}px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #4b5563;
  white-space: ${({ preLine }) => (preLine ? 'pre-line' : 'normal')};
`;

const Divider = styled.div`
  height: 1px;
  background: #f2f3f5;
  margin: 0 16px;

  &:last-child {
    display: none;
  }
`;

const ResultTable = ({
  products,
  quantities,
  allSelected,
  someSelected,
  selectedCount,
  onToggleAll,
  onToggleRow,
  onQuantityChange,
  onChangeProduct,
  onSwapProduct,
  loggedIn,
  goToSignIn,
}: ResultTableProps) => {
  const subtitleText = translations(messages.selectedHighConfidenceCount).replace('{count}', String(selectedCount));

  return (
    <Wrapper>
      <HeaderRow>
        <Checkbox checked={allSelected} indeterminate={someSelected} onClick={onToggleAll} aria-label="select all" />
        <TitleCol>
          <TitleText>{translations(messages.recommendedProducts)}</TitleText>
          <SubtitleText>{subtitleText}</SubtitleText>
        </TitleCol>
        <ColLabel width={100} preLine>
          {translations(messages.columnUnitPrice)}
        </ColLabel>
        <ColLabel width={106}>{translations(messages.columnConfidence)}</ColLabel>
        <ColLabel width={140}>{translations(messages.columnQuantity)}</ColLabel>
      </HeaderRow>
      <Divider />
      {products.map((product, idx) => (
        <Fragment key={product.id}>
          <ResultRow
            product={product}
            quantity={quantities[product.id] ?? product.defaultQuantity}
            onToggle={onToggleRow}
            onQuantityChange={onQuantityChange}
            onChangeProduct={onChangeProduct}
            onSwapProduct={onSwapProduct}
            loggedIn={loggedIn}
            goToSignIn={goToSignIn}
          />
          {idx < products.length - 1 && <Divider />}
        </Fragment>
      ))}
    </Wrapper>
  );
};

export default memo(ResultTable);
